import { useEffect, useRef, useState } from "react";

interface ImageWithFallbackProps {
  fallback: string;
  log?: (...data: any[]) => void;
}

const imgSrcLoadSuccess: Map<string, boolean> = new Map();
const imgSrcLoading: Map<string, Promise<boolean>> = new Map();

export default function ImageWithFallback({
  src,
  fallback,
  log,
  alt,
  ...otherProps
}: ImageWithFallbackProps & React.ImgHTMLAttributes<HTMLImageElement>) {
  const isRemoved = useRef(false);
  useEffect(() => {
    isRemoved.current = false;
    return () => {
      isRemoved.current = true;
    };
  }, []);

  const [imgSrc, setImgSrc] = useState(() => {
    if (!src) return undefined;
    const loadSuccess = imgSrcLoadSuccess.get(src);
    if (loadSuccess === true) return src;
    if (loadSuccess === false) return undefined;
    return undefined;
  });

  useEffect(() => {
    if (!src) return;
    if (src === imgSrc) return;

    const resolveImgSrc = async () => {
      let loadSuccess = imgSrcLoadSuccess.get(src);

      if (loadSuccess == undefined) {
        let loading = imgSrcLoading.get(src);
        if (loading == undefined) {
          loading = new Promise((resolve) => {
            log?.(`[ImageWithFallback] loading ${src} -> start`);
            const img = new Image();
            img.onload = () => {
              log?.(`[ImageWithFallback] loading ${src} -> SUCCESS`);
              resolve(true);
            };
            img.onerror = () => {
              log?.(`[ImageWithFallback] loading ${src} -> FAILURE`);
              resolve(false);
            };
            img.src = src;
          });
          imgSrcLoading.set(src, loading);
        }
        loadSuccess = await loading;
        imgSrcLoading.delete(src);
        imgSrcLoadSuccess.set(src, loadSuccess);
      }

      if (!isRemoved.current && loadSuccess === true) setImgSrc(src);
      if (!isRemoved.current && loadSuccess === false) setImgSrc(undefined);
    };
    resolveImgSrc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  return <img src={imgSrc ?? fallback} alt={alt ?? ""} {...otherProps} />;
}
